import React, { useState, useEffect } from 'react';
import { formatDate, formatTime } from '../../utils/dateHelpers';
import { useAuth } from '../../hooks/useAuth';
import EnhancedInterviewScheduleModal from './EnhancedInterviewScheduleModal';
import FeedbackModal from './FeedbackModal';
import supabaseService from '../../services/supabaseService';

const InterviewDetailModal = ({ interview, isOpen, onClose, onStatusChange }) => {
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [isSavingFeedback, setIsSavingFeedback] = useState(false);
  const [pendingReschedule, setPendingReschedule] = useState(null);
  const [isLoadingReschedule, setIsLoadingReschedule] = useState(false);
  const [isProcessingReschedule, setIsProcessingReschedule] = useState(false);
  const { user, userProfile } = useAuth();
  const isVA = userProfile?.role === 'va';
  const isEmployer = userProfile?.role === 'employer';

  // Load pending reschedule request when modal opens
  useEffect(() => {
    if (isOpen && interview?.id) {
      loadPendingRescheduleRequest();
    }
  }, [isOpen, interview?.id]);

  const loadPendingRescheduleRequest = async () => {
    setIsLoadingReschedule(true);
    try {
      const { rescheduleRequest, error } = await supabaseService.interviews.getPendingRescheduleRequest(interview.id);
      if (!error && rescheduleRequest) {
        setPendingReschedule(rescheduleRequest);
      } else {
        setPendingReschedule(null);
      }
    } catch (error) {
      console.error('[InterviewDetailModal] Error loading reschedule request:', error);
    } finally {
      setIsLoadingReschedule(false);
    }
  };

  const handleApproveReschedule = async () => {
    if (!pendingReschedule) return;

    setIsProcessingReschedule(true);
    try {
      const { interview: updatedInterview, error } = await supabaseService.interviews.approveRescheduleRequest(pendingReschedule.id);
      if (error) {
        throw new Error(error);
      }

      // Refresh reschedule request status
      await loadPendingRescheduleRequest();
      
      // Notify parent component of the change
      if (onStatusChange) {
        await onStatusChange(interview.id, 'rescheduled');
      }

      window.alert('Reschedule request approved successfully!');
    } catch (error) {
      console.error('[InterviewDetailModal] Error approving reschedule:', error);
      window.alert('Failed to approve reschedule request: ' + error.message);
    } finally {
      setIsProcessingReschedule(false);
    }
  };

  const handleRejectReschedule = async () => {
    if (!pendingReschedule) return;

    // Ask for reason using window.prompt (browser native)
    const reason = window.prompt('Please provide a reason for rejecting this reschedule request (optional):');
    if (reason === null) return; // User cancelled

    setIsProcessingReschedule(true);
    try {
      const { error } = await supabaseService.interviews.rejectRescheduleRequest(pendingReschedule.id, reason);
      if (error) {
        throw new Error(error);
      }

      // Refresh reschedule request status
      await loadPendingRescheduleRequest();

      window.alert('Reschedule request rejected.');
    } catch (error) {
      console.error('[InterviewDetailModal] Error rejecting reschedule:', error);
      window.alert('Failed to reject reschedule request: ' + error.message);
    } finally {
      setIsProcessingReschedule(false);
    }
  };

  if (!isOpen || !interview) return null;

  // Development logging
  if (process.env.NODE_ENV === 'development') {
    console.log('[InterviewDetailModal] Opened for interview:', interview);
    console.log('[InterviewDetailModal] Pending reschedule:', pendingReschedule);
  }

  const handleStatusUpdate = async (newStatus) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[InterviewDetailModal] Updating status:', {
        interviewId: interview.id,
        currentStatus: interview.status,
        newStatus: newStatus
      });
    }

    // Handle reschedule differently by showing the scheduler modal
    if (newStatus === 'rescheduled') {
      setShowRescheduleModal(true);
      return;
    }

    // For completion status changes, open the FeedbackModal
    if (newStatus === 'completed' && interview.status !== 'completed') {
      setShowFeedbackModal(true);
      return;
    }

    setIsUpdatingStatus(true);
    try {
      await onStatusChange(interview.id, newStatus);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('[InterviewDetailModal] Status updated successfully:', {
          interviewId: interview.id,
          newStatus: newStatus
        });
      }
      
      // Only close on successful update
      onClose();
    } catch (error) {
      console.error('[InterviewDetailModal] Error updating interview status:', {
        interviewId: interview.id,
        newStatus: newStatus,
        error: error.message || error
      });
      // Keep modal open on error so user can retry
      // Could add a toast notification here for better UX
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      scheduled: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      rescheduled: 'bg-yellow-100 text-yellow-800',
      pending: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getNextStatusOptions = (currentStatus) => {
    // Only employers (interviewers) can complete interviews and provide feedback
    const canCompleteInterview = userProfile?.role === 'employer' && 
                                 (interview.interviewer_id === user?.id || interview.employer_id === user?.id);
    
    switch (currentStatus) {
      case 'scheduled': {
        const options = [];
        
        // Only employers can complete interviews (to provide feedback)
        if (canCompleteInterview) {
          options.push({ value: 'completed', label: 'Mark as Completed', color: 'bg-green-600 hover:bg-green-700' });
        }
        
        // Both parties can request reschedule and cancellation, but only interviewers can confirm
        if (canCompleteInterview) {
          options.push({ value: 'cancelled', label: 'Cancel Interview', color: 'bg-red-600 hover:bg-red-700' });
          options.push({ value: 'rescheduled', label: 'Reschedule', color: 'bg-yellow-600 hover:bg-yellow-700' });
        }
        
        return options;
      }
        
      case 'pending':
        if (canCompleteInterview) {
          return [
            { value: 'scheduled', label: 'Confirm Schedule', color: 'text-white hover:opacity-90', style: { background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' } },
            { value: 'cancelled', label: 'Cancel Interview', color: 'bg-red-600 hover:bg-red-700' }
          ];
        }
        return [];
        
      case 'rescheduled':
        if (canCompleteInterview) {
          return [
            { value: 'scheduled', label: 'Confirm New Schedule', color: 'text-white hover:opacity-90', style: { background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' } },
            { value: 'cancelled', label: 'Cancel Interview', color: 'bg-red-600 hover:bg-red-700' }
          ];
        }
        return [];
        
      default:
        return [];
    }
  };

  const handleRescheduleClose = () => {
    setShowRescheduleModal(false);
  };

  const handleFeedbackSubmit = async (feedbackData) => {
    setIsSavingFeedback(true);
    try {
      const { interview: updatedInterview, error } = await supabaseService.interviews.completeInterview(interview.id, feedbackData);
      if (error) throw new Error(error);

      setShowFeedbackModal(false);

      if (onStatusChange) {
        await onStatusChange(interview.id, 'completed');
      }

      onClose();
    } catch (error) {
      console.error('[InterviewDetailModal] Error completing interview with feedback:', error);
      throw error; // FeedbackModal handles displaying the error
    } finally {
      setIsSavingFeedback(false);
    }
  };

  const handleRescheduleSuccess = async (interviewData) => {
    // Close the reschedule modal and the detail modal
    setShowRescheduleModal(false);
    
    // Update the interview status to 'rescheduled' and refresh the parent
    try {
      await onStatusChange(interview.id, 'rescheduled', interviewData);
      onClose();
    } catch (error) {
      console.error('[InterviewDetailModal] Error updating rescheduled interview:', error);
    }
  };

  const statusOptions = getNextStatusOptions(interview.status);
  
  // Check if assessment data is available
  const hasAssessmentData = Boolean(
    interview.rating || 
    interview.score || 
    interview.overall_rating || 
    interview.interview_rating ||
    interview.feedback ||
    interview.strengths ||
    interview.areas_for_improvement ||
    interview.skills_assessment
  );

  // Show feedback modal exclusively (no stacked modals)
  if (showFeedbackModal) {
    return (
      <FeedbackModal
        show={true}
        onClose={() => setShowFeedbackModal(false)}
        onSubmit={handleFeedbackSubmit}
        interview={interview}
        loading={isSavingFeedback}
      />
    );
  }

  // Show reschedule modal if it's open
  if (showRescheduleModal) {
    return (
      <EnhancedInterviewScheduleModal
        isOpen={true}
        onClose={handleRescheduleClose}
        onSubmit={handleRescheduleSuccess}
        initialData={{
          ...interview,
          jobId: interview.job?.id || interview.job_id,
          employerId: interview.employer?.id || interview.employer_id,
          employerName: interview.employer?.full_name || interview.employer_name,
          jobTitle: interview.job?.title || interview.job_title || interview.jobTitle,
          candidateId: interview.applicant?.id || interview.candidate?.id || interview.candidate_id,
          candidateName: interview.applicant?.full_name || interview.candidate?.full_name || interview.candidate_name,
          applicationId: interview.application_id
        }}
        selectedCandidate={{
          id: interview.applicant?.id || interview.candidate?.id || interview.candidate_id,
          name: interview.applicant?.full_name || interview.candidate?.full_name || interview.candidate_name,
          email: interview.applicant?.email || interview.candidate?.email || interview.candidate_email
        }}
        currentUserId={user?.id}
      />
    );
  }

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-start p-6 border-b">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Interview Details</h2>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(interview.status)}`}>
              {interview.status.charAt(0).toUpperCase() + interview.status.slice(1)}
            </span>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close modal"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Job Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Job Information</h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div>
                <span className="font-medium text-gray-700">Position:</span>
                <span className="ml-2 text-gray-900">
                  {interview.job?.title || interview.job_title || interview.jobTitle || 'N/A'}
                </span>
              </div>
              {isVA && (
                <div>
                  <span className="font-medium text-gray-700">Company:</span>
                  <span className="ml-2 text-gray-900">
                    {interview.job?.company_name || 
                     interview.employer?.company_name || 
                     interview.companyName || 
                     interview.company_name || 
                     'N/A'}
                  </span>
                </div>
              )}
              {/* <div>
                <span className="font-medium text-gray-700">Location:</span>
                <span className="ml-2 text-gray-900">
                  {interview.job?.location || interview.location || 'N/A'}
                </span>
              </div> */}
            </div>
          </div>

          {/* Interview Schedule */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Schedule</h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div>
                <span className="font-medium text-gray-700">Date:</span>
                <span className="ml-2 text-gray-900">{formatDate(interview.scheduled_at)}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Time:</span>
                <span className="ml-2 text-gray-900">{formatTime(interview.scheduled_at)}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Duration:</span>
                <span className="ml-2 text-gray-900">
                  {interview.duration || interview.duration_minutes || 60} minutes
                </span>
              </div>
              {(interview.meeting_link || interview.meetingLink) && (
                <div>
                  <span className="font-medium text-gray-700">Meeting Link:</span>
                  <a 
                    href={interview.meeting_link || interview.meetingLink} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="ml-2 text-blue-600 hover:text-blue-800 underline"
                  >
                    Join Meeting
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Pending Reschedule Request */}
          {!isLoadingReschedule && pendingReschedule && (
            <div className="border-l-4 border-yellow-500 bg-yellow-50 p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-medium text-yellow-800">
                    {isEmployer ? 'Reschedule Request Pending Your Approval' : 'Your Reschedule Request is Pending'}
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700 space-y-1">
                    <div>
                      <span className="font-medium">Requested by:</span>{' '}
                      {pendingReschedule.requested_by === user?.id ? 'You' : 'Candidate'}
                    </div>
                    <div>
                      <span className="font-medium">Current Date:</span>{' '}
                      {formatDate(pendingReschedule.original_scheduled_at)} at {formatTime(pendingReschedule.original_scheduled_at)}
                    </div>
                    <div>
                      <span className="font-medium">Proposed Date:</span>{' '}
                      {formatDate(pendingReschedule.new_scheduled_at)} at {formatTime(pendingReschedule.new_scheduled_at)}
                    </div>
                    {pendingReschedule.reason && (
                      <div>
                        <span className="font-medium">Reason:</span>{' '}
                        {pendingReschedule.reason}
                      </div>
                    )}
                  </div>
                  
                  {/* Action buttons for employer */}
                  {isEmployer && interview.interviewer_id === user?.id && (
                    <div className="mt-4 flex space-x-3">
                      <button
                        onClick={handleApproveReschedule}
                        disabled={isProcessingReschedule}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isProcessingReschedule ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Processing...
                          </>
                        ) : (
                          <>
                            <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Approve
                          </>
                        )}
                      </button>
                      <button
                        onClick={handleRejectReschedule}
                        disabled={isProcessingReschedule}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isProcessingReschedule ? 'Processing...' : (
                          <>
                            <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            Decline
                          </>
                        )}
                      </button>
                    </div>
                  )}

                  {/* Status message for VA */}
                  {isVA && pendingReschedule.requested_by === user?.id && (
                    <div className="mt-3 text-sm text-yellow-700">
                      <p>Waiting for employer to review your reschedule request.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Interview Assessment - Only show for completed interviews */}
          {interview.status === 'completed' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Interview Assessment</h3>
              
              {/* Add debugging info in development */}
              {process.env.NODE_ENV === 'development' && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-xs">
                  <strong>Debug Info:</strong>
                  <pre className="mt-2 whitespace-pre-wrap">{JSON.stringify({
                    feedbackObject: interview.feedback,
                    feedbackType: typeof interview.feedback,
                    rating: interview.rating,
                    score: interview.score,
                    overall_rating: interview.overall_rating,
                    interview_rating: interview.interview_rating,
                    allInterviewKeys: Object.keys(interview)
                  }, null, 2)}</pre>
                </div>
              )}

              <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-4">
                
                {/* Rating - Always show rating section, handle null values */}
                <div>
                  <span className="font-medium text-gray-700">Overall Rating:</span>
                  <div className="flex items-center space-x-2 mt-1">
                    {(() => {
                      // Handle feedback as a JSON object
                      let ratingValue;
                      if (interview.feedback && typeof interview.feedback === 'object') {
                        ratingValue = interview.feedback.rating;
                      } else {
                        ratingValue = interview.rating || interview.score || interview.overall_rating || interview.interview_rating;
                      }
                      
                      // If we have a valid numeric rating, show stars
                      if (ratingValue && !isNaN(ratingValue) && ratingValue > 0) {
                        return (
                          <>
                            <div className="flex items-center">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <svg
                                  key={star}
                                  className={`w-5 h-5 ${
                                    star <= ratingValue
                                      ? 'text-yellow-400'
                                      : 'text-gray-300'
                                  }`}
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                              ))}
                            </div>
                            <span className="text-sm text-gray-600">
                              ({ratingValue}/5)
                            </span>
                          </>
                        );
                      } else {
                        // Show "Not rated yet" message
                        return (
                          <div className="flex items-center space-x-2">
                            <div className="flex items-center">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <svg
                                  key={star}
                                  className="w-5 h-5 text-gray-300"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                              ))}
                            </div>
                            <span className="text-sm text-gray-500 italic">
                              Not rated yet
                            </span>
                          </div>
                        );
                      }
                    })()}
                  </div>
                </div>

                {/* Feedback */}
                {interview.feedback && (
                  <div>
                    <span className="font-medium text-gray-700">Feedback:</span>
                    <div className="mt-1 p-3 bg-white rounded-md border">
                      <p className="text-gray-700 whitespace-pre-wrap">
                        {typeof interview.feedback === 'object' ? interview.feedback.feedback : interview.feedback}
                      </p>
                    </div>
                  </div>
                )}

                {/* Technical Skills Rating */}
                {interview.feedback && typeof interview.feedback === 'object' && interview.feedback.technical_skills && (
                  <div>
                    <span className="font-medium text-gray-700">Technical Skills:</span>
                    <div className="flex items-center space-x-2 mt-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <svg
                          key={star}
                          className={`w-4 h-4 ${
                            star <= interview.feedback.technical_skills ? 'text-yellow-400' : 'text-gray-300'
                          }`}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                      <span className="text-xs text-gray-500 ml-1">({interview.feedback.technical_skills}/5)</span>
                    </div>
                  </div>
                )}

                {/* Communication Skills Rating */}
                {interview.feedback && typeof interview.feedback === 'object' && interview.feedback.communication_skills && (
                  <div>
                    <span className="font-medium text-gray-700">Communication Skills:</span>
                    <div className="flex items-center space-x-2 mt-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <svg
                          key={star}
                          className={`w-4 h-4 ${
                            star <= interview.feedback.communication_skills ? 'text-yellow-400' : 'text-gray-300'
                          }`}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                      <span className="text-xs text-gray-500 ml-1">({interview.feedback.communication_skills}/5)</span>
                    </div>
                  </div>
                )}

                {/* Cultural Fit Rating */}
                {interview.feedback && typeof interview.feedback === 'object' && interview.feedback.cultural_fit && (
                  <div>
                    <span className="font-medium text-gray-700">Cultural Fit:</span>
                    <div className="flex items-center space-x-2 mt-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <svg
                          key={star}
                          className={`w-4 h-4 ${
                            star <= interview.feedback.cultural_fit ? 'text-yellow-400' : 'text-gray-300'
                          }`}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                      <span className="text-xs text-gray-500 ml-1">({interview.feedback.cultural_fit}/5)</span>
                    </div>
                  </div>
                )}

                {/* Strengths */}
                {(() => {
                  const strengths = interview.feedback && typeof interview.feedback === 'object' 
                    ? interview.feedback.strengths 
                    : interview.strengths;
                  return strengths ? (
                    <div>
                      <span className="font-medium text-gray-700">Strengths:</span>
                      <div className="mt-1 p-3 bg-white rounded-md border">
                        {Array.isArray(strengths) ? (
                          <ul className="list-disc list-inside space-y-1">
                            {strengths.map((strength, index) => (
                              <li key={index} className="text-gray-700">{strength}</li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-gray-700 whitespace-pre-wrap">{strengths}</p>
                        )}
                      </div>
                    </div>
                  ) : null;
                })()}

                {/* Areas for Improvement */}
                {(() => {
                  const improvements = interview.feedback && typeof interview.feedback === 'object' 
                    ? interview.feedback.areas_for_improvement 
                    : interview.areas_for_improvement;
                  return improvements ? (
                    <div>
                      <span className="font-medium text-gray-700">Areas for Improvement:</span>
                      <div className="mt-1 p-3 bg-white rounded-md border">
                        {Array.isArray(improvements) ? (
                          <ul className="list-disc list-inside space-y-1">
                            {improvements.map((area, index) => (
                              <li key={index} className="text-gray-700">{area}</li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-gray-700 whitespace-pre-wrap">{improvements}</p>
                        )}
                      </div>
                    </div>
                  ) : null;
                })()}

                {/* Skills Assessment */}
                {interview.skills_assessment && (
                  <div>
                    <span className="font-medium text-gray-700">Skills Assessment:</span>
                    <div className="mt-1 p-3 bg-white rounded-md border">
                      {typeof interview.skills_assessment === 'object' && !Array.isArray(interview.skills_assessment) ? (
                        <div className="space-y-2">
                          {Object.entries(interview.skills_assessment).map(([skill, rating]) => (
                            <div key={skill} className="flex justify-between items-center">
                              <span className="text-gray-700 capitalize">{skill.replace('_', ' ')}:</span>
                              <div className="flex items-center space-x-1">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <svg
                                    key={star}
                                    className={`w-4 h-4 ${
                                      star <= rating ? 'text-yellow-400' : 'text-gray-300'
                                    }`}
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                  </svg>
                                ))}
                                <span className="text-xs text-gray-500 ml-1">({rating}/5)</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-700 whitespace-pre-wrap">{interview.skills_assessment}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Hiring Decision */}
                {(interview.hiring_status || interview.hiring_decision) && (
                  <div>
                    <span className="font-medium text-gray-700">Hiring Decision:</span>
                    <div className="mt-1 flex items-center space-x-2">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        (interview.hiring_status === 'hired' || interview.hiring_decision === 'hire')
                          ? 'bg-green-100 text-green-800'
                          : (interview.hiring_status === 'not_moving_forward' || interview.hiring_decision === 'no_hire')
                          ? 'bg-red-100 text-red-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {(interview.hiring_status || interview.hiring_decision)
                          .replace('_', ' ')
                          .replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                    </div>
                  </div>
                )}

                {/* Offer Details */}
                {interview.offer_amount && (
                  <div>
                    <span className="font-medium text-gray-700">Offer Amount:</span>
                    <span className="ml-2 text-green-600 font-semibold">
                      ${interview.offer_amount}/hr
                    </span>
                  </div>
                )}

                {/* Hiring Notes */}
                {interview.hiring_notes && (
                  <div>
                    <span className="font-medium text-gray-700">Hiring Notes:</span>
                    <div className="mt-1 p-3 bg-white rounded-md border">
                      <p className="text-gray-700 whitespace-pre-wrap">{interview.hiring_notes}</p>
                    </div>
                  </div>
                )}

                {/* Interview Date Completed */}
                {interview.completed_at && (
                  <div>
                    <span className="font-medium text-gray-700">Completed On:</span>
                    <span className="ml-2 text-gray-900">
                      {formatDate(interview.completed_at)} at {formatTime(interview.completed_at)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Participants */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Participants</h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 font-medium">
                    {(interview.applicant?.full_name || interview.candidate?.full_name || interview.candidate_name)?.charAt(0) || 'VA'}
                  </span>
                </div>
                <div>
                  <div className="font-medium text-gray-900">
                    {interview.applicant?.full_name || interview.candidate?.full_name || interview.candidate_name || 'Virtual Assistant'}
                  </div>
                  <div className="text-sm text-gray-600">
                    {interview.applicant?.email || interview.candidate?.email || interview.candidate_email || 'Applicant'}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-green-600 font-medium">
                    {(interview.employer?.full_name || interview.employerName)?.charAt(0) || 'E'}
                  </span>
                </div>
                <div>
                  <div className="font-medium text-gray-900">
                    {interview.employer?.full_name || interview.employerName || 'Employer'}
                  </div>
                  <div className="text-sm text-gray-600">
                    {interview.employer?.email || 'Interviewer'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          {interview.notes && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Notes</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-700 whitespace-pre-wrap">{interview.notes}</p>
              </div>
            </div>
          )}

          {/* Status Update Actions */}
          {statusOptions.length > 0 && (
            <div>
              <div className="flex flex-wrap gap-2">
                {statusOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleStatusUpdate(option.value)}
                    disabled={isUpdatingStatus && option.value !== 'rescheduled'}
                    className={`px-4 py-2 text-white font-medium rounded-lg transition-colors ${option.color} ${
                      (isUpdatingStatus && option.value !== 'rescheduled') ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    style={option.style || {}}
                  >
                    {(isUpdatingStatus && option.value !== 'rescheduled') ? 'Updating...' : option.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Information for VAs when they can't perform actions */}
          {statusOptions.length === 0 && interview.status === 'scheduled' && userProfile?.role === 'va' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-blue-900 mb-1">Interview Status</h4>
                  <p className="text-sm text-blue-800">
                    The employer will mark this interview as completed and provide feedback after your interview. 
                    You'll be able to see the assessment details once they're available.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Information for completed interviews without assessment data */}
          {interview.status === 'completed' && userProfile?.role === 'va' && !hasAssessmentData && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <svg className="w-5 h-5 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-amber-900 mb-1">Assessment Pending</h4>
                  <p className="text-sm text-amber-800">
                    This interview has been completed, but the employer hasn't provided assessment feedback yet. 
                    Assessment details will appear here once they're available.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>

    </div>
  );
};

export default InterviewDetailModal;